// src/components/ThemeCustomizer/ThemeCustomizer.jsx
import React from 'react';
import { useThemeManager } from '../../hooks/useThemeManager';
import './ThemeCustomizer.css';

const ThemeCustomizer = ({ eventId, onThemeChange }) => {
  const {
    theme,
    updateColor,
    updateTypography,
    applyPreset,
    resetTheme,
    availablePresets,
    isEditing,
    setIsEditing
  } = useThemeManager(eventId);

  const handleColorChange = (colorKey, value) => {
    updateColor(colorKey, value);
    onThemeChange?.({ ...theme, colors: { ...theme.colors, [colorKey]: value } });
  };

  const handleFontChange = (fontKey, value) => {
    updateTypography(fontKey, value);
    onThemeChange?.({ ...theme, typography: { ...theme.typography, [fontKey]: value } });
  };

  return (
    <div className="theme-customizer">
      <div className="customizer-header">
        <h3>Theme Customization</h3>
        <button 
          className="toggle-btn"
          onClick={() => setIsEditing(!isEditing)}
        >
          {isEditing ? 'Close' : 'Customize Theme'}
        </button>
      </div>

      {isEditing && (
        <div className="customizer-panel">
          {/* Preset Themes */}
          <div className="preset-section">
            <h4>Quick Presets</h4>
            <div className="preset-grid">
              {Object.entries(availablePresets).map(([key, preset]) => (
                <button
                  key={key}
                  className="preset-btn"
                  onClick={() => applyPreset(key)}
                  style={{
                    background: `linear-gradient(135deg, ${preset.colors.primary}, ${preset.colors.secondary})`
                  }}
                  title={preset.name}
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>

          {/* Color Customization */}
          <div className="color-section">
            <h4>Colors</h4>
            {Object.entries(theme.colors).map(([colorKey, colorValue]) => (
              <div key={colorKey} className="color-input-group">
                <label>{colorKey}</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    value={colorValue}
                    onChange={(e) => handleColorChange(colorKey, e.target.value)}
                    className="color-picker"
                  />
                  <input
                    type="text"
                    value={colorValue}
                    onChange={(e) => handleColorChange(colorKey, e.target.value)}
                    className="color-text"
                  />
                </div>
                <div 
                  className="color-preview"
                  style={{ backgroundColor: colorValue }}
                />
              </div>
            ))}
          </div>

          {/* Typography Customization */}
          <div className="typography-section">
            <h4>Typography</h4>
            <div className="font-input-group">
              <label>Primary Font</label>
              <select
                value={theme.typography?.primary}
                onChange={(e) => handleFontChange('primary', e.target.value)}
              >
                <option value="Inter, sans-serif">Inter</option>
                <option value="Roboto, sans-serif">Roboto</option>
                <option value="Open Sans, sans-serif">Open Sans</option>
                <option value="Georgia, serif">Georgia</option>
                <option value="Times New Roman, serif">Times New Roman</option>
              </select>
            </div>

            <div className="font-input-group">
              <label>Base Font Size</label>
              <select
                value={theme.typography?.baseSize}
                onChange={(e) => handleFontChange('baseSize', e.target.value)}
              >
                <option value="14px">Small (14px)</option>
                <option value="16px">Medium (16px)</option>
                <option value="18px">Large (18px)</option>
                <option value="20px">Extra Large (20px)</option>
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="customizer-actions">
            <button onClick={resetTheme} className="reset-btn">
              Reset to Default
            </button>
            <button onClick={() => setIsEditing(false)} className="save-btn">
              Apply Changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeCustomizer;